/**
 * Nesting the release timeline: a flat, version-ordered list of `releases`
 * rows into LINES (10.2) and the BUILDS shipped under them (10.2.0.27).
 *
 * Pure, and in its own module, because three things depend on getting it
 * right — the timeline, its pagination and the "latest" chip — and none of
 * them should have to mount a component to be sure of it.
 */
import { compareVersions } from "../../../pages/documentation/utils.js";

import type { ReleaseDTO } from "./releases-api.js";

/** Newest first, by version NUMBER (10.1.0 after 9.3.0, not before). */
export function sortReleasesNewestFirst(rows: readonly ReleaseDTO[]): ReleaseDTO[] {
  return [...rows].sort((a, b) => compareVersions(b.version, a.version));
}

/** A release line and the builds shipped under it, newest first. */
export interface ReleaseFamily {
  /** The line — or, for a build whose line is not in this list, the build
   *  itself, so nothing is ever dropped for want of a parent. */
  line: ReleaseDTO;
  builds: ReleaseDTO[];
  /** False when `line` is really an orphan build standing in for one. */
  hasLine: boolean;
}

/**
 * The versions inside a release folder.
 *
 * For an orphan — a version whose release is not in the list — that is the
 * version itself, standing alone in a folder of its own name. Dropping it
 * because its parent is missing would hide shipped media; showing an empty
 * folder would be a lie about what it holds.
 */
export function versionsOf(family: ReleaseFamily): ReleaseDTO[] {
  return family.hasLine ? family.builds : [family.line];
}

/** The folder a `?release=` param names, by the version printed on it. */
export function findFamily(
  families: readonly ReleaseFamily[],
  version: string | null,
): ReleaseFamily | undefined {
  if (!version) return undefined;
  return families.find((f) => f.line.version === version);
}

/**
 * Nest the flat, version-ordered list the API returns into families.
 *
 * Families are ordered by the newest version in each, not by the line's own
 * version: a line is "10.2" while its builds are "10.2.0.27", and sorting on
 * the line alone would rank a family below one whose line number is higher
 * but whose builds are older.
 */
export function groupIntoFamilies(rows: readonly ReleaseDTO[]): ReleaseFamily[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  // `== null` on purpose: during a rolling deploy the SPA can be newer than
  // portal-api, which then omits the field entirely. A release with no parent
  // — null or absent — is a line.
  const lines = rows.filter((r) => r.parent_release_id == null);
  const buildsByLine = new Map<number, ReleaseDTO[]>();
  const orphans: ReleaseDTO[] = [];

  for (const row of rows) {
    const parentId = row.parent_release_id;
    if (parentId == null) continue;
    if (!byId.has(parentId)) {
      orphans.push(row);
      continue;
    }
    const list = buildsByLine.get(parentId);
    if (list) list.push(row);
    else buildsByLine.set(parentId, [row]);
  }

  const families: ReleaseFamily[] = lines.map((line) => ({
    line,
    builds: sortReleasesNewestFirst(buildsByLine.get(line.id) ?? []),
    hasLine: true,
  }));
  for (const orphan of orphans) {
    families.push({ line: orphan, builds: [], hasLine: false });
  }

  const newestOf = (f: ReleaseFamily): string => f.builds[0]?.version ?? f.line.version;
  return families.sort((a, b) => compareVersions(newestOf(b), newestOf(a)));
}
